import { useLoaderData, useNavigate } from 'react-router-dom';
import Table from '../components/table';
import { householdAdminAccess } from '../utils/authRoleWrap';
import GetAllCampaigns from '../services/adapters/allCampaigns';
import { DisplayCampaignInformation } from '../types/campaigns.type';
import Button from '../components/buttons/baseButton';
import DownloadButton from '../components/buttons/downloadButton';
import { householdAdminRoles, Benefit, CampaignStatus } from '../types/enums';
import { ActionColumn } from '../types/table.type';
import columns from '../components/campaigns/campaignTableColumns';
import RenderReleaseCampaignButton from '../components/campaigns/releaseCampaignButton';
import useAuth from '../utils/auth';
import { getCampaignCsvUrl } from '../utils/helpers';

const CreateCampaignButton = householdAdminAccess(Button);

export function loader() {
  const allCampaignInstance = new GetAllCampaigns();
  const campaignData = allCampaignInstance.adaptResponse();
  return campaignData;
}

export default function Campaigns() {
  const allCampaigns = useLoaderData<DisplayCampaignInformation[]>();
  const authUser = useAuth();
  const navigate = useNavigate();
  const campaignButton = (
    <CreateCampaignButton
      id="create-new-campaign-button"
      className="button-green"
      label="Create new campaign"
      onClick={() => navigate('/campaigns/new')}
    />
  );
  const benefitAnalysisTypes = [Benefit.PensionCredit, Benefit.AttendanceAllowance];

  const handleNavigate = (row: DisplayCampaignInformation) => {
    const selectedCampaign = allCampaigns.find((campaign) => campaign.campaignCommunicationId === row.campaignCommunicationId);
    if (!selectedCampaign) {
      return null;
    }
    if (benefitAnalysisTypes.includes(selectedCampaign.campaignType as Benefit) && selectedCampaign.status === CampaignStatus.Complete) {
      navigate(`/campaigns/${row.campaignCommunicationId}`);
    }
    return null;
  };

  const renderSendReminderButton = (data: DisplayCampaignInformation) => (data.status === CampaignStatus.Complete ? (
    <Button
      id={`campaign-table${data.campaignCommunicationId}`}
      label="send reminder"
      className="!text-xs button-default !pointer-events-auto"
      onClick={(e) => { e.stopPropagation(); navigate(`/campaigns/${data.campaignCommunicationId}/reminder`); }}
    />
  ) : null);

  const renderOrganisationAdminColumn = (): ActionColumn<DisplayCampaignInformation> [] => {
    if (householdAdminRoles.includes(authUser.role)) {
      return [{
        key: 'reviewAndSend',
        header: 'Review & Send',
        render: (data) => RenderReleaseCampaignButton(data, data.reviewed ? 'Send' : 'Review'),
      }, {
        key: 'sendReminder',
        header: 'Send Reminder',
        render: (data) => renderSendReminderButton(data),
      }];
    }
    return [];
  };

  const actionColumns: ActionColumn<DisplayCampaignInformation>[] = [
    {
      key: 'export',
      header: 'Export',
      render: (data) => (
        <DownloadButton
          id={`campaign-table${data.campaignCommunicationId}`}
          className="!text-xs button-default !pointer-events-auto"
          fetchUrl={() => getCampaignCsvUrl(data)}
        />
      ),
    },
    ...renderOrganisationAdminColumn(),
  ];

  return (
    allCampaigns.length > 0
      ? (
        <div id="campaign-container" className="flex flex-col w-full p-4 overflow-auto">
          <div className="flex flex-row justify-between border-b-2 items-center">
            <h1 id="campaign-header" className="m-2 p-4 text-header font-medium text-primary-dark ">Campaigns</h1>
            {campaignButton}
          </div>
          <Table
            data={allCampaigns}
            columns={columns}
            actionColumns={actionColumns}
            title="Campaigns"
            onRowClick={handleNavigate}
            rowClassName={(
              campaign: DisplayCampaignInformation,
            ) => (benefitAnalysisTypes.includes(campaign.campaignType as Benefit)
              && campaign.status === CampaignStatus.Complete
              ? 'hover:bg-pipgrey-light'
              : 'cursor-default')}
            tBodyClassName="text-sm"
          />
        </div>
      ) : (
        <div className="flex flex-col overflow-auto justify-center items-center absolute inset-0 m-auto p-4 border-0 rounded-md shadow-lg bg-white h-72 w-1/2 text-center">
          <p className="m-2 p-2">You have not started any campaigns yet.</p>
          {campaignButton}
        </div>
      )
  );
}
